import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Check, RotateCw } from 'lucide-react';

const QuizMode = ({ groups, onClose }) => {
  const [selectedGroups, setSelectedGroups] = useState(groups.length === 1 ? groups : []);
  const [isSelecting, setIsSelecting] = useState(groups.length > 1);
  const [cards, setCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoadingNewCard, setIsLoadingNewCard] = useState(false);
  const cardRef = useRef(null);
  const [dragStart, setDragStart] = useState(null);
  const [offset, setOffset] = useState(0);
  const [displayedCard, setDisplayedCard] = useState(null);
  const [nextCard, setNextCard] = useState(null);

  useEffect(() => {
    // Only auto-start the quiz if we have a single group
    if (groups.length === 1) {
      const allCards = groups.flatMap(group => 
        group.people?.filter(person => person && !person.deleted).map(person => ({
          firstName: person.firstName || 'Unknown',
          lastName: person.lastName || 'Unknown',
          ...person,
          groupName: group.name
        })) || []
      );
      if (allCards.length === 0) {
        console.error('No valid cards found in group');
        return;
      }
      const shuffled = [...allCards].sort(() => Math.random() - 0.5);
      setCards(shuffled);
      setDisplayedCard(shuffled[0]);
    }
  }, [groups]);

  useEffect(() => {
    if (isLoadingNewCard && nextCard) {
      const timer = setTimeout(() => {
        setDisplayedCard(nextCard);
        setNextCard(null);
        setIsLoadingNewCard(false);
        setIsTransitioning(false);
      }, 300); // Match with CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isLoadingNewCard, nextCard]);

  const startQuiz = () => {
    if (selectedGroups.length === 0) {
      console.error('No groups selected');
      return;
    }
    
    const allCards = selectedGroups.flatMap(group => {
      console.log('Processing group:', group.name, group.people);
      return (group.people || [])
        .filter(person => person && !person.deleted)
        .map(person => {
          console.log('Person data:', person);
          const { firstName, lastName, ...rest } = person;  // Destructure to handle separately
          return {
            firstName: firstName || 'Unknown',
            lastName: lastName || 'Unknown',
            ...rest,  // Spread the rest of the properties
            groupName: group.name
          };
        });
    });
    
    if (allCards.length === 0) {
      console.error('No valid cards found in selected groups');
      return;
    }
    
    const shuffled = [...allCards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setDisplayedCard(shuffled[0]);
    console.log('Loaded cards for multiple groups:', shuffled);
    setIsSelecting(false);
    setCurrentCardIndex(0);
  };

  const handleGroupToggle = (group) => {
    setSelectedGroups(prev => 
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e) => {
    if (!dragStart) return;
    const touch = e.touches[0];
    const diff = touch.clientX - dragStart.x;
    setOffset(diff);
  };

  const handleTouchEnd = () => {
    if (Math.abs(offset) > 100) {
      if (offset > 0) {
        handleCardTransition('right');
      } else {
        handleCardTransition('left');
      }
    }
    setDragStart(null);
    setOffset(0);
  };

  const handleCardTransition = (direction) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setIsFlipped(false);
    setIsLoadingNewCard(true);

    if (direction === 'right') {
      const remainingCards = cards.filter((_, i) => i !== currentCardIndex);
      setCards(remainingCards);
      setNextCard(remainingCards[0]);
    } else {
      const reorderedCards = [
        ...cards.slice(0, currentCardIndex),
        ...cards.slice(currentCardIndex + 1),
        cards[currentCardIndex]
      ];
      setCards(reorderedCards);
      setNextCard(reorderedCards[0]);
    }
  };

  if (isSelecting && groups.length > 1) {
    return (
      <div className="p-4 space-y-4">
        <h2 className="text-xl font-bold">Select Groups for Quiz</h2>
        <div className="space-y-2">
          {groups.map(group => (
            <button
              key={group.id}
              onClick={() => handleGroupToggle(group)}
              className={`w-full p-2 rounded transition-colors ${
                selectedGroups.includes(group)
                  ? 'bg-[#04b53a] text-white'
                  : 'border border-[#04b53a] text-[#04b53a] hover:bg-[#04b53a] hover:text-white'
              }`}
            >
              {group.name}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={startQuiz}
            disabled={selectedGroups.length === 0}
            className={`w-full p-2 rounded ${
              selectedGroups.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#04b53a] text-white hover:bg-[#039431]'
            }`}
          >
            Start Quiz
          </button>
          <button 
            onClick={onClose}
            className="w-full p-2 rounded border border-[#04b53a] text-[#04b53a] hover:bg-[#04b53a] hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="p-4 text-center space-y-4">
        <h2 className="text-xl font-bold">Quiz Complete!</h2>
        <p>You've successfully identified all the cards.</p>
        <button 
          onClick={onClose}
          className="w-full p-2 rounded bg-[#04b53a] text-white hover:bg-[#039431]"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 h-full flex flex-col max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-gray-500">
          Cards remaining: {cards.length}
        </span>
        <button 
          onClick={onClose}
          className="px-3 py-1 rounded border border-[#04b53a] text-[#04b53a] hover:bg-[#04b53a] hover:text-white text-sm"
        >
          Exit Quiz
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center">
        <div 
          className="w-full max-w-md mx-auto"
          onClick={() => !isTransitioning && setIsFlipped(!isFlipped)}
        >
          <div className={`relative [perspective:1000px]`}>
            {/* Front face */}
            <div 
              className={`w-full bg-white rounded-lg shadow-lg flex flex-col items-center justify-center p-8 transition-all duration-300 ${
                isFlipped ? '[transform:rotateY(180deg)] opacity-0' : 'opacity-100'
              }`}
              style={{ transform: `translateX(${offset}px)` }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="flex flex-col items-center justify-center">
                {displayedCard?.imageUrl ? (
                  <div className="w-48 h-48 mb-4 relative rounded-full overflow-hidden">
                    <img
                      src={displayedCard.imageUrl}
                      alt="Card"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-48 h-48 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                    <span className="text-gray-400">No image available</span>
                  </div>
                )}
                <p className="text-gray-500 mt-4">Tap to flip</p>
              </div>
            </div>

            {/* Back face */}
            <div 
              className={`absolute inset-0 w-full bg-white rounded-lg shadow-lg flex items-center justify-center p-8 transition-all duration-300 ${
                isFlipped ? 'opacity-100 [transform:rotateY(0deg)]' : '[transform:rotateY(-180deg)] opacity-0'
              }`}
            >
              {!isTransitioning && displayedCard && (
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-bold">
                    {`${displayedCard.firstName || 'Unknown'} ${displayedCard.lastName || ''}`}
                  </h3>
                  <p className="text-gray-600">{displayedCard.groupName}</p>
                  {displayedCard.details && (
                    <p className="text-gray-700">{displayedCard.details}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-4 gap-4">
        <button
          className="flex-1 p-3 rounded border border-[#04b53a] text-[#04b53a] hover:bg-[#04b53a] hover:text-white flex items-center justify-center"
          onClick={() => !isTransitioning && handleCardTransition('left')}
          disabled={isTransitioning}
        >
          <X className="mr-2" />
          Don't Know
        </button>
        <button
          className="flex-1 p-3 rounded bg-[#04b53a] text-white hover:bg-[#039431] flex items-center justify-center"
          onClick={() => !isTransitioning && handleCardTransition('right')}
          disabled={isTransitioning}
        >
          <Check className="mr-2" />
          Know It
        </button>
      </div>
    </div>
  );
};

export default QuizMode;